import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { FocusScope } from "@radix-ui/react-focus-scope";
import { ChevronDown, Check, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ComboBoxSearch — required single-select with typeahead search.
 * No "All" option. Ideal for GRN line-item vendor/material pickers.
 *
 * The dropdown is rendered via a React portal so that ancestor overflow
 * clipping (e.g. `overflow-x-auto` on a scroll container, `overflow-hidden`
 * on a card) never hides suggestions. Position is recomputed on every open
 * plus on window scroll/resize while open.
 *
 * Props:
 *   value       — currently selected option value (string) or null
 *   onChange    — (nextValue|null) => void
 *   options     — [{ value, label, hint?, sub?, disabled? }]
 *                 `sub` renders as a small secondary line under the label
 *   placeholder — trigger text when nothing selected
 *   testId
 *   clearable   — show × when a value is selected
 *   className
 *   size        — 'sm' (h-9) | 'md' (h-10)
 */
export default function ComboBoxSearch({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  testId,
  clearable = false,
  className,
  size = "sm",
  disabled = false,
  emptyLabel = "No matches",
  onSearchChange = null,     // when set, parent handles filtering server-side
  loading = false,           // shown while parent is refetching
  searchDebounceMs = 250,
  // After a value is picked, focus the element with this data-testid so the
  // form keeps flowing under a keyboard-only workflow. The GRN uses this to
  // jump vendor → payment status, RM → qty, etc.
  nextFocusTestId = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState(null);
  const [highlight, setHighlight] = useState(0);   // keyboard-highlighted option index
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const popRef = useRef(null);
  const listRef = useRef(null);

  // If the parent wants to drive the option list from the server, debounce
  // the query and hand it up. This keeps the picker fast for huge datasets
  // (10k+ raw materials) while still supporting a simple client-side mode
  // when `onSearchChange` isn't provided.
  useEffect(() => {
    if (!onSearchChange) return;
    const t = setTimeout(() => onSearchChange(query.trim()), searchDebounceMs);
    return () => clearTimeout(t);
  }, [query, onSearchChange, searchDebounceMs]);

  // Recompute the portal popover position when the trigger moves (scroll,
  // resize, layout shifts). We use fixed positioning so ancestors with
  // `overflow: hidden` can never clip the dropdown.
  const updateRect = () => {
    if (!rootRef.current) return;
    setRect(rootRef.current.getBoundingClientRect());
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateRect();
    const handler = () => updateRect();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => {
      const insideTrigger = rootRef.current && rootRef.current.contains(e.target);
      const insidePop = popRef.current && popRef.current.contains(e.target);
      if (!insideTrigger && !insidePop) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 20);
  }, [open]);

  // When the picker is opened inside a Radix Dialog (or any container with an
  // active FocusScope / DismissableLayer), the parent's document-level
  // `focusin` listener will snap focus back out of our portalled search input
  // — making typing impossible — and its `pointerdown` listener will treat
  // clicks in our popover as "outside" and dismiss the dialog. Stopping
  // propagation of just those two native events on the popover container
  // keeps the parent scope unaware, while React's synthetic click/keydown
  // handlers continue to fire normally.
  // When the picker is opened inside a Radix Dialog (or any container with an
  // active FocusScope / DismissableLayer), we need to prevent the parent
  // scope from stealing focus back and from treating clicks inside our
  // portalled popover as "outside" (which would dismiss the Dialog). We use
  // Radix's own FocusScope inside the popover — mounting a new scope pauses
  // the parent scope, so our search input can hold focus.
  //
  // We still need to stop `pointerdown` from bubbling to the document so that
  // Radix's outside-click detection on the Dialog doesn't dismiss it.
  useEffect(() => {
    if (!open || !rect || !popRef.current) return;
    const stop = (e) => e.stopPropagation();
    const el = popRef.current;
    el.addEventListener("pointerdown", stop);
    return () => {
      el.removeEventListener("pointerdown", stop);
    };
  }, [open, rect]);

  // Reset keyboard highlight when the visible list changes.
  useEffect(() => { setHighlight(0); }, [query, options]);

  // Helper used by both the mouse-click and keyboard-Enter paths so both
  // routes commit and jump focus consistently.
  const commit = (value) => {
    onChange(value);
    setOpen(false);
    setQuery("");
    if (nextFocusTestId) {
      // Defer to allow React to re-render and enable the target field.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="${nextFocusTestId}"]`);
        if (el && !el.disabled) {
          el.focus();
          if (typeof el.select === "function") el.select();
        }
      }, 30);
    }
  };

  // Keyboard nav: ↓/↑ move highlight, Enter selects, Escape closes.
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt && !opt.disabled) commit(opt.value);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      rootRef.current?.querySelector("button")?.focus();
    }
  };

  // Scroll highlighted option into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-hl-idx="${highlight}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open]);

  const filtered = useMemo(() => {
    // When the parent is driving the list from the server, don't filter
    // again on the client — the server already applied the search.
    if (onSearchChange) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(q) ||
        (o.hint || "").toLowerCase().includes(q) ||
        (o.sub || "").toLowerCase().includes(q)
    );
  }, [options, query, onSearchChange]);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  // Cache the most-recent full option we selected so that when the parent
  // refetches options (server-side typeahead), the trigger doesn't blank out
  // just because the selected value is no longer in the current list.
  const lastSelectedRef = useRef(null);
  useEffect(() => {
    if (selected) lastSelectedRef.current = selected;
    if (value == null) lastSelectedRef.current = null;
  }, [selected, value]);
  const displayed = selected || (value != null && lastSelectedRef.current?.value === value ? lastSelectedRef.current : null);

  const heightCls = size === "md" ? "h-10" : "h-9";

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        disabled={disabled}
        data-testid={testId ? `${testId}-trigger` : undefined}
        className={cn(
          "w-full px-2.5 rounded-md border border-gray-200 bg-white text-left text-sm",
          heightCls,
          "flex items-center gap-2",
          "hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#9B1C1C]/30",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        {displayed ? (
          <span className="flex-1 truncate text-gray-900">{displayed.label}</span>
        ) : (
          <span className="flex-1 truncate text-gray-400">{placeholder}</span>
        )}
        {clearable && displayed && (
          <X
            className="h-3.5 w-3.5 text-gray-400 hover:text-gray-700"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
          />
        )}
        <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
      </button>

      {open && rect && createPortal(
        <FocusScope asChild trapped={false} loop={false}>
          <div
            ref={popRef}
            className="fixed z-[1000] rounded-md border border-gray-200 bg-white shadow-xl max-h-80 overflow-hidden flex flex-col"
            style={{
              top: rect.bottom + 4,
              left: rect.left,
              width: Math.max(rect.width, 240),
              pointerEvents: "auto",
            }}
            data-testid={testId ? `${testId}-popover` : undefined}
          >
          <div className="relative border-b border-gray-100">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Type to search…"
              className="w-full h-9 pl-8 pr-2 text-sm outline-none bg-transparent"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="overflow-y-auto flex-1" ref={listRef}>
            {loading ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">Searching…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-xs text-gray-400 text-center">{emptyLabel}</p>
            ) : (
              filtered.map((o, idx) => (
                <button
                  key={o.value}
                  type="button"
                  disabled={o.disabled}
                  data-hl-idx={idx}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => {
                    if (o.disabled) return;
                    commit(o.value);
                  }}
                  className={cn(
                    "w-full flex items-start gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                    idx === highlight && "bg-gray-100",
                    o.value === value ? "bg-red-50 text-[#9B1C1C]" : "hover:bg-gray-50",
                    o.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                >
                  {o.value === value ? (
                    <Check className="h-3.5 w-3.5 mt-0.5" />
                  ) : (
                    <span className="w-3.5" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{o.label}</span>
                    {o.sub && (
                      <span className="block truncate text-[11px] text-gray-500">{o.sub}</span>
                    )}
                  </span>
                  {o.hint && (
                    <span className="text-[11px] text-gray-500 ml-2 whitespace-nowrap">{o.hint}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
        </FocusScope>,
        document.body
      )}
    </div>
  );
}
